import { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react'
import { INSI_CONSULT_APP_URL } from '../../../environment/insiConsult'
import { insiConsultPatientMapper } from '../../../misc/insi.utilities'
import { InsiConnectInsResponse } from '../../../model/Insi'
import { insiConsultRequestAtom } from '../../../state/insi'
import { IconButton, Modal } from '../../shared'
import { InsiConsultIframeProps } from './InsiConsultIframe.model'
import { useAtom } from 'jotai'
import { useCreateInsiLogs } from '../../../hooks/queries/insi'
import styles from './InsiConsultIframe.module.scss'
import { isDefined } from '@follow/cdk'
import type { InsStatusCheckTypeValue } from '../../../features/ins/types/ins.types'
import { InsStatusCheckTypeEnums } from '../../../features/ins/enums/insStatusCheckType.enums'

export const InsiConsultIframe: FunctionComponent<InsiConsultIframeProps> = () => {
  const [insiConsultRequest] = useAtom(insiConsultRequestAtom)

  const insiConsultRef = useRef<HTMLIFrameElement>(null)
  const { mutate: logInsi } = useCreateInsiLogs()

  const insiConsultMessageHandler = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== INSI_CONSULT_APP_URL.origin) return

      logInsi({ payload: event.data })

      const response: InsiConnectInsResponse = event.data

      const wasCheckCanceled = !isDefined(response.CR)
      if (wasCheckCanceled) {
        return
      }

      const isSuccess =
        [InsStatusCheckTypeEnums['00'], InsStatusCheckTypeEnums['01'], InsStatusCheckTypeEnums['02']].includes(response.CR?.codeCR ?? "");
      const patient = insiConsultPatientMapper(response)

      if (isSuccess) {
        const insResponse = {
          patient,
          codeCR: response?.CR?.codeCR as InsStatusCheckTypeValue || null,
        }
        insiConsultRequest?.onReceivePatient(insResponse)
      } else {
        console.error('InsiConsult :', event.data)
        insiConsultRequest?.onError({
          error: response?.error,
          codeCR: response?.CR?.codeCR,
        })
      }
    },
    [insiConsultRequest, logInsi],
  )

  useEffect(() => {
    window.addEventListener('message', insiConsultMessageHandler)
    return () => {
      window.removeEventListener('message', insiConsultMessageHandler)
    }
  }, [insiConsultMessageHandler])

  const requestSearch = useMemo(() => {
    if (!insiConsultRequest?.search) return ''

    const params = btoa(JSON.stringify({ search: insiConsultRequest.search }))
    return `#params=${params}`
  }, [insiConsultRequest])

  return (
    <Modal
      hidden={!insiConsultRequest}
      isUnmounting={false}
      onClickOutside={() => {
        insiConsultRequest?.onReceivePatient(null)
      }}
    >
      <div className={styles.container}>
        <div className={styles.backWrapper}>
          <IconButton
            onClick={() => insiConsultRequest?.onReceivePatient(null)}
            icon="back"
            theme="light"
            size="normal"
            testId="button-insi_iframe_exit"
          />
          <span className={styles.backReturnText}>Retour sur Follow</span>
        </div>

        <iframe
          allow="camera; loopback-network *; local-network *; local-network-access *"
          className={styles.iframe}
          ref={insiConsultRef}
          title="insi-consult-iframe"
          src={`${INSI_CONSULT_APP_URL.toString()}${requestSearch}`}
        />
      </div>
    </Modal>
  )
}
